import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectAccessToken } from "../../store/authSlice";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { SidebarCol } from "../../screens/chatMain/Sidebar/Sidebar.jsx";
import { MainHeader } from "../../screens/chatMain/MainHeader/MainHeader.jsx";
import {
  validateAssessments,
  upsertAssessments,
} from "../../api/bulkUpload/assessmentsApi.js";
import {
  validateCheckins,
  upsertCheckins,
} from "../../api/bulkUpload/checkinsApi.js";
import {
  validateLiveEventEnrollments,
  upsertLiveEventEnrollments,
} from "../../api/bulkUpload/liveEventEnrollmentsApi.js";
import {
  validateMindfulness,
  upsertMindfulness,
} from "../../api/bulkUpload/mindfulnessApi.js";
import { getTemplateDownloadUrl } from "../../api/bulkUpload/templatesApi.js";
import {
  validateBulkOnboard,
  upsertBulkOnboard,
} from "../../api/dev/bulkOnboardApi.js";
import { buildBulkOnboardPayload } from "../../utils/bulkOnboardTransform.js";

// ─── Date helpers ────────────────────────────────────────────────────────────

const excelSerialToDateTime = (serial) => {
  const excelEpoch = new Date(Date.UTC(1899, 11, 30));
  return new Date(excelEpoch.getTime() + serial * 86400000);
};

const formatDate = (date) => date.toISOString().split("T")[0];

const formatDateTime = (date) => {
  const pad = (v) => String(v).padStart(2, "0");
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(
    date.getUTCDate()
  )} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(
    date.getUTCSeconds()
  )}`;
};

const excelDateToJSDate = (serial) => formatDate(excelSerialToDateTime(serial));
const excelDateToJSDateTime = (serial) =>
  formatDateTime(excelSerialToDateTime(serial));

// ─── Row transforms ──────────────────────────────────────────────────────────

const transformCheckinRows = (rows, type) =>
  rows.map((row, index) => {
    let formattedDate = row.checkin_date;
    if (typeof row.checkin_date === "number") {
      formattedDate = excelDateToJSDate(row.checkin_date);
    }
    const payload = {
      row_number: index + 1,
      user_id: Number(row.user_id),
      checkin_date: formattedDate,
      type,
      answers: [
        { question_no: 1, answer_number: Number(row.question_1) },
        { question_no: 2, answer_number: Number(row.question_2) },
        { question_no: 3, answer_number: Number(row.question_3) },
      ],
    };
    if (type === "MORNING") {
      payload.answers.push({ question_no: 4, answer_domain: row.question_4_domain });
    }
    return payload;
  });

const transformAssessmentRows = (rows) =>
  rows.map((row, index) => {
    const normalizedRow = { ...row, row_number: index + 1 };
    if (typeof normalizedRow.assessment_date === "number") {
      normalizedRow.assessment_date = excelDateToJSDate(normalizedRow.assessment_date);
    } else if (typeof normalizedRow.date === "number") {
      normalizedRow.date = excelDateToJSDate(normalizedRow.date);
    }
    return normalizedRow;
  });

const transformMindfulnessRows = (rows) =>
  rows.map((row, index) => {
    const normalizedRow = { ...row, row_number: index + 1 };
    if (typeof normalizedRow.date === "number") {
      normalizedRow.date = excelDateToJSDate(normalizedRow.date);
    } else if (typeof normalizedRow.Date === "number") {
      normalizedRow.Date = excelDateToJSDate(normalizedRow.Date);
    }
    if (normalizedRow.user_id !== undefined && normalizedRow.user_id !== "") {
      normalizedRow.user_id = Number(normalizedRow.user_id);
    }
    return normalizedRow;
  });

const transformLiveEventEnrollmentRows = (rows) =>
  rows.map((row, index) => {
    const normalizedRow = { ...row, row_number: index + 1 };
    if (typeof normalizedRow.date === "number") {
      normalizedRow.date = excelDateToJSDateTime(normalizedRow.date);
    }
    if (normalizedRow.event_id !== undefined && normalizedRow.event_id !== "") {
      normalizedRow.event_id = Number(normalizedRow.event_id);
    }
    if (normalizedRow.user_id !== undefined && normalizedRow.user_id !== "") {
      normalizedRow.user_id = Number(normalizedRow.user_id);
    }
    return normalizedRow;
  });

const createBulkPayload = (uploadKey, rows) => ({
  upload_id: `${uploadKey}-${Date.now()}`,
  options: { allow_partial_success: true },
  rows,
});

// ─── UploadCard presentational component ─────────────────────────────────────

const UploadCard = ({
  title,
  subtitle,
  uploadKey,
  selectedFile,
  checkinType,
  fileInputRef,
  loading,
  downloadingTemplate,
  validationErrors,
  onFileChange,
  onClearFile,
  onUpload,
  onDownloadTemplate,
  onCheckinTypeChange,
}) => {
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) onFileChange(e.dataTransfer.files[0]);
  };

  const rejectedRows =
    validationErrors?.filter((r) => r.status !== "accepted") ?? [];

  return (
    <Paper
      data-testid={`upload-card-${uploadKey}`}
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: "1px solid #e5e7eb",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "100%",
      }}
    >
      {/* Card header */}
      <Box>
        <Typography variant="subtitle1" fontWeight={700}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      </Box>

      {/* Download template */}
      {onDownloadTemplate && (
        <Button
          variant="outlined"
          size="small"
          startIcon={
            downloadingTemplate ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <DownloadIcon />
            )
          }
          disabled={!!downloadingTemplate}
          onClick={onDownloadTemplate}
          sx={{ alignSelf: "flex-start", borderRadius: 2, textTransform: "none" }}
        >
          {downloadingTemplate ? "Downloading…" : "Download Template"}
        </Button>
      )}

      {uploadKey === "checkins" && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Check-in type
          </Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            value={checkinType || null}
            onChange={(_, nextValue) => onCheckinTypeChange?.(nextValue)}
            aria-label="check-in type"
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 1,
              "& .MuiToggleButton-root": {
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
              },
            }}
          >
            <ToggleButton value="MORNING" aria-label="Morning check-in">
              Morning
            </ToggleButton>
            <ToggleButton value="NIGHT" aria-label="Night check-in">
              Night
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Drop zone or file preview */}
      {!selectedFile ? (
        <Box
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            border: "2px dashed",
            borderColor: dragActive ? "primary.main" : "#d1d5db",
            borderRadius: 2,
            p: 3,
            textAlign: "center",
            cursor: "pointer",
            bgcolor: dragActive ? "#EEF2FF" : "transparent",
            transition: "all 0.2s",
            "&:hover": {
              borderColor: "primary.main",
              bgcolor: "#F5F3FF",
            },
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={(e) => onFileChange(e.target.files?.[0] ?? null)}
            style={{ display: "none" }}
          />
          <CloudUploadIcon sx={{ fontSize: 40, color: "#9ca3af", mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Drag &amp; drop or click to select
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Supported: CSV, XLSX, XLS
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            border: "1px solid #e5e7eb",
            borderRadius: 2,
            p: 2,
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            <CheckCircleOutlineIcon sx={{ color: "success.main", flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                fontWeight={500}
                noWrap
                title={selectedFile.name}
              >
                {selectedFile.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {(selectedFile.size / 1024).toFixed(2)} KB
              </Typography>
            </Box>
          </Box>
          <Button
            size="small"
            color="error"
            variant="text"
            startIcon={<DeleteOutlineIcon />}
            onClick={onClearFile}
            disabled={loading}
            sx={{ flexShrink: 0, textTransform: "none" }}
          >
            Clear
          </Button>
        </Box>
      )}

      {/* Row-level validation errors */}
      <Collapse in={rejectedRows.length > 0}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
            Validation failed — {rejectedRows.length} rejected row
            {rejectedRows.length !== 1 ? "s" : ""}
          </Typography>
          <TableContainer
            component={Box}
            sx={{ maxHeight: 200, overflowY: "auto" }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Row</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rejectedRows.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{row.row_number ?? row.row ?? "–"}</TableCell>
                    <TableCell>
                      {Array.isArray(row.errors)
                        ? row.errors.join(", ")
                        : row.error ?? row.message ?? "Unknown error"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Alert>
      </Collapse>

      {/* Upload button */}
      <Button
        data-testid={`upload-button-${uploadKey}`}
        variant="contained"
        disabled={!selectedFile || loading}
        onClick={onUpload}
        startIcon={
          loading ? <CircularProgress size={16} color="inherit" /> : <InsertDriveFileOutlinedIcon />
        }
        aria-label={`Upload ${title} data`}
        sx={{
          mt: "auto",
          borderRadius: 2,
          textTransform: "none",
          fontWeight: 600,
        }}
      >
        {loading ? "Uploading…" : "Upload & Add Data"}
      </Button>
    </Paper>
  );
};

// ─── Main AddData page ────────────────────────────────────────────────────────

const AddData = () => {
  const navigate = useNavigate();
  const accessToken = useSelector(selectAccessToken);

  const [files, setFiles] = useState({
    assessments: null,
    liveEventEnrollments: null,
    checkins: null,
    mindfulness: null,
    bulkOnboard: null,
  });
  const [loadingKey, setLoadingKey] = useState(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [checkinType, setCheckinType] = useState("");
  const [companyConfirmDialog, setCompanyConfirmDialog] = useState({
    open: false,
    companyName: "",
    companyCode: "",
    existingCount: 0,
    newCount: 0,
    existingEmails: [],
    newEmails: [],
    payload: null,
  });

  const handleConfirmExistingCompany = async () => {
    if (!companyConfirmDialog?.payload) return;
    const payload = {
      ...companyConfirmDialog.payload,
      allow_existing_company: true,
    };
    setLoadingKey("bulkOnboard");
    setCompanyConfirmDialog((prev) => ({ ...prev, open: false }));
    try {
      const res = await upsertBulkOnboard(payload, accessToken);
      const summary = res?.summary;
      if (summary?.skipped_rows > 0) {
        toast.success(
          `Added ${summary.inserted_rows} new users to ${companyConfirmDialog.companyName}! (${summary.skipped_rows} existing emails skipped)`,
          { autoClose: 7000 }
        );
      } else {
        toast.success(
          res?.message || `Successfully updated ${companyConfirmDialog.companyName}!`
        );
      }
      clearSelectedFile("bulkOnboard");
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to update existing company."
      );
    } finally {
      setLoadingKey(null);
    }
  };

  const handleCancelExistingCompany = () => {
    setCompanyConfirmDialog((prev) => ({ ...prev, open: false }));
    clearFileInputOnly("bulkOnboard");
    toast.info("Upload canceled. Please update the company name in your template and try again.");
  };


  const assessmentFileInputRef = useRef(null);
  const liveEventEnrollmentsFileInputRef = useRef(null);
  const checkinsFileInputRef = useRef(null);
  const mindfulnessFileInputRef = useRef(null);
  const bulkOnboardFileInputRef = useRef(null);

  const fileInputRefs = {
    assessments: assessmentFileInputRef,
    liveEventEnrollments: liveEventEnrollmentsFileInputRef,
    checkins: checkinsFileInputRef,
    mindfulness: mindfulnessFileInputRef,
    bulkOnboard: bulkOnboardFileInputRef,
  };

  // ── File helpers ────────────────────────────────────────────────────────────

  const handleFileChange = (key) => (file) => {
    setFiles((prev) => ({ ...prev, [key]: file }));
    setValidationErrors((prev) => ({ ...prev, [key]: null }));
  };

  const clearSelectedFile = (key) => {
    setFiles((prev) => ({ ...prev, [key]: null }));
    setValidationErrors((prev) => ({ ...prev, [key]: null }));
    const ref = fileInputRefs[key];
    if (ref?.current) ref.current.value = "";
  };

  const clearFileInputOnly = (key) => {
    setFiles((prev) => ({ ...prev, [key]: null }));
    const ref = fileInputRefs[key];
    if (ref?.current) ref.current.value = "";
  };

  // ── Template download ───────────────────────────────────────────────────────

  const getTemplateErrorMessage = (err) => {
    const status = err?.response?.status;
    const msg = err?.response?.data?.message;
    if (status === 400) return msg || "Invalid template module.";
    if (status === 401 || status === 403) return msg || "Not authorised to download this template.";
    if (status === 404) return msg || "Template file not found.";
    if (status === 500) return msg || "Failed to generate template download URL.";
    return msg || "Failed to download template. Please try again.";
  };

  const handleDownloadTemplate = async (module) => {
    try {
      setDownloadingTemplate(module);
      const { url, filename } = await getTemplateDownloadUrl(module, accessToken);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    } catch (err) {
      console.error(err);
      toast.error(getTemplateErrorMessage(err));
    } finally {
      setDownloadingTemplate(null);
    }
  };

  // ── Payload builders ────────────────────────────────────────────────────────

  const buildCheckinPayloads = (workbook, selectedFile, selectedType) => {
    const normalizedType = String(selectedType || "").trim().toUpperCase();
    if (!normalizedType) {
      throw new Error("Please select a Morning or Night check-in type.");
    }
    if (!["MORNING", "NIGHT"].includes(normalizedType)) {
      throw new Error("Invalid check-in type.");
    }

    return workbook.SheetNames.map((sheetName) => {
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
      return createBulkPayload(
        normalizedType.toLowerCase(),
        transformCheckinRows(jsonData, normalizedType)
      );
    });
  };

  const buildAssessmentPayloads = (workbook) =>
    workbook.SheetNames.map((sheetName, index) => {
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
      return createBulkPayload(`assessments-${index + 1}`, transformAssessmentRows(jsonData));
    });

  const buildMindfulnessPayloads = (workbook) =>
    workbook.SheetNames.map((sheetName, index) => {
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
      return createBulkPayload(`mindfulness-${index + 1}`, transformMindfulnessRows(jsonData));
    });

  const buildLiveEventEnrollmentPayloads = (workbook) =>
    workbook.SheetNames.map((sheetName, index) => {
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
      return createBulkPayload(
        `live-event-enrollments-${index + 1}`,
        transformLiveEventEnrollmentRows(jsonData)
      );
    });

  // ── Upload configs ──────────────────────────────────────────────────────────

  const uploadConfigs = {
    assessments: {
      validate: validateAssessments,
      upsert: upsertAssessments,
      buildPayloads: buildAssessmentPayloads,
      successMessage: "Assessment data uploaded successfully!",
      failureMessage:
        "Assessment validation failed. Fix the rejected rows and upload again.",
    },
    liveEventEnrollments: {
      validate: validateLiveEventEnrollments,
      upsert: upsertLiveEventEnrollments,
      buildPayloads: buildLiveEventEnrollmentPayloads,
      successMessage: "Live event enrollments uploaded successfully!",
      failureMessage:
        "Live event enrollment validation failed. Fix the rejected rows and upload again.",
    },
    checkins: {
      validate: validateCheckins,
      upsert: upsertCheckins,
      buildPayloads: buildCheckinPayloads,
      successMessage: "Check-in data uploaded successfully!",
      failureMessage:
        "Check-in validation failed. Fix the rejected rows and upload again.",
    },
    mindfulness: {
      validate: validateMindfulness,
      upsert: upsertMindfulness,
      buildPayloads: buildMindfulnessPayloads,
      successMessage: "Mindfulness records uploaded successfully!",
      failureMessage:
        "Mindfulness validation failed. Fix the rejected rows and upload again.",
    },
    bulkOnboard: {
      validate: validateBulkOnboard,
      upsert: upsertBulkOnboard,
      successMessage: "Company, superuser, and employees created successfully!",
      failureMessage:
        "Bulk onboarding failed. Please fix the errors and try again.",
    },
  };

  // ── Upload handler ──────────────────────────────────────────────────────────

  const handleUpload = (uploadKey) => async () => {
    const selectedFile = files[uploadKey];
    if (!selectedFile) {
      toast.error("Please select a CSV or Excel file first.");
      return;
    }

    if (uploadKey === "checkins" && !checkinType) {
      toast.error("Please select a Morning or Night check-in type first.");
      return;
    }

    const config = uploadConfigs[uploadKey];

    setLoadingKey(uploadKey);
    setValidationErrors((prev) => ({ ...prev, [uploadKey]: null }));

    const reader = new FileReader();

    reader.onerror = () => {
      toast.error("Failed to read file. Please try again.");
      setLoadingKey(null);
    };

    reader.onload = async (evt) => {
      try {
        const data = evt.target.result;
        const workbook = XLSX.read(data, { type: "binary" });

        let allPayloads;
        if (uploadKey === "bulkOnboard") {
          allPayloads = [buildBulkOnboardPayload(workbook, Date.now())];
        } else if (uploadKey === "checkins") {
          allPayloads = config.buildPayloads(workbook, selectedFile, checkinType);
        } else {
          allPayloads = config.buildPayloads(workbook, selectedFile);
        }

        for (const payload of allPayloads) {
          const validateResponse = await config.validate(payload, accessToken);
          const rowResults = validateResponse?.row_results ?? [];

          const rejectedRows = rowResults.filter((r) => r.status !== "accepted");

          if (rejectedRows.length > 0) {
            setValidationErrors((prev) => ({
              ...prev,
              [uploadKey]: rowResults,
            }));
            toast.error(config.failureMessage);
            clearFileInputOnly(uploadKey);
            return;
          }

          if (uploadKey === "bulkOnboard" && validateResponse?.company_exists) {
            if (!validateResponse.company_code_provided) {
              toast.error(
                `Company "${validateResponse.company_details?.company_name}" already exists. To add users to this company, please include "company_code" in your template, or use a different company name.`,
                { autoClose: 8000 }
              );
              clearFileInputOnly(uploadKey);
              return;
            }
            if (validateResponse.company_code_valid === false) {
              toast.error(
                validateResponse.company_code_message || "Company code does not match existing company.",
                { autoClose: 8000 }
              );
              clearFileInputOnly(uploadKey);
              return;
            }

            setCompanyConfirmDialog({
              open: true,
              companyName: validateResponse.company_details?.company_name,
              companyCode: payload.company?.company_code,
              existingCount: validateResponse.existing_emails_count || 0,
              newCount: validateResponse.new_emails_count || 0,
              existingEmails: validateResponse.existing_emails || [],
              newEmails: validateResponse.new_emails || [],
              payload: payload,
            });
            return;
          }

          const upsertRes = await config.upsert(payload, accessToken);
          if (uploadKey === "bulkOnboard" && upsertRes?.summary?.skipped_rows > 0) {
            toast.success(
              `Added ${upsertRes.summary.inserted_rows} users. ${upsertRes.summary.skipped_rows} existing emails were skipped.`,
              { autoClose: 6000 }
            );
          } else {
            toast.success(config.successMessage);
          }
        }

        clearSelectedFile(uploadKey);

      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Validation or upload failed. Please check your file and try again."
        );
      } finally {
        setLoadingKey(null);
      }
    };

    reader.readAsBinaryString(selectedFile);
  };

  // ── Card definitions ────────────────────────────────────────────────────────

  const cardDefs = [
    {
      key: "assessments",
      title: "Assessments",
      subtitle: "CSV or Excel files supported",
      templateModule: "assessments",
      ref: assessmentFileInputRef,
    },
    {
      key: "liveEventEnrollments",
      title: "Live Event Enrollments",
      subtitle: "CSV or Excel files supported",
      templateModule: "live-event-enrollments",
      ref: liveEventEnrollmentsFileInputRef,
    },
    {
      key: "mindfulness",
      title: "Mindfulness",
      subtitle: "CSV or Excel files supported",
      templateModule: "mindfulness",
      ref: mindfulnessFileInputRef,
    },
    {
      key: "checkins",
      title: "Check-ins",
      subtitle: "CSV or Excel files supported",
      templateModule: "checkins",
      ref: checkinsFileInputRef,
    },
    {
      key: "bulkOnboard",
      title: "Bulk Onboard Users",
      subtitle: "Upload CSV/Excel to create companies, admins, and employees",
      templateModule: "bulk-onboard",
      ref: bulkOnboardFileInputRef,
    },
  ];

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Box
      sx={{
        display: "flex",
        width: "100vw",
        height: "100vh",
        maxWidth: "100vw",
        maxHeight: "100vh",
        overflow: "hidden",
        background: "#F8FAFC",
      }}
    >
      {/* Sidebar */}
      <Box sx={{ flexShrink: 0 }}>
        <SidebarCol />
      </Box>

      {/* Main area */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "hidden",
        }}
      >
        <MainHeader
          page="dev"
          titleText="Add Bulk Data"
          subtitleText="Upload CSV or Excel data"
          back={() => {
            if (window.history.state && window.history.state.idx > 0) {
              navigate(-1);
            } else {
              navigate("/dev");
            }
          }}
        />

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 3,
            pb: 4,
            pt: 3,
          }}
        >
          {/* Breadcrumb */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 4 }}>
            <Typography variant="caption" sx={{ color: "#9ca3af" }}>
              Home
            </Typography>
            <Typography variant="caption" sx={{ color: "#9ca3af" }}>
              /
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "#9ca3af", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
              onClick={() => navigate("/dev")}
            >
              Developer
            </Typography>
            <Typography variant="caption" sx={{ color: "#9ca3af" }}>
              /
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "#374151", fontWeight: 500 }}
            >
              Add Data
            </Typography>
          </Box>

          {/* Upload cards */}
          <Grid container spacing={3}>
            {cardDefs.map(({ key, title, subtitle, templateModule, ref }) => (
              <Grid item xs={12} sm={6} lg={4} key={key}>
                <UploadCard
                  title={title}
                  subtitle={subtitle}
                  uploadKey={key}
                  selectedFile={files[key]}
                  checkinType={key === "checkins" ? checkinType : undefined}
                  fileInputRef={ref}
                  loading={loadingKey === key}
                  downloadingTemplate={
                    downloadingTemplate === templateModule ? templateModule : null
                  }
                  validationErrors={validationErrors[key]}
                  onFileChange={handleFileChange(key)}
                  onClearFile={() => clearSelectedFile(key)}
                  onUpload={handleUpload(key)}
                  onDownloadTemplate={() => handleDownloadTemplate(templateModule)}
                  onCheckinTypeChange={key === "checkins" ? setCheckinType : undefined}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* Confirmation Dialog for Existing Company */}
      <Dialog
        open={Boolean(companyConfirmDialog.open)}
        onClose={handleCancelExistingCompany}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 1 },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1.25rem", color: "#1e293b", pb: 1 }}>
          Company Already Exists
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2, color: "#475569", fontSize: "0.95rem" }}>
            The company <strong>"{companyConfirmDialog.companyName}"</strong> already exists in the system.
          </DialogContentText>
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: "#f8fafc",
              border: "1px solid #e2e8f0",
              mb: 2.5,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#166534" }}>
                • {companyConfirmDialog.newCount} New User(s):
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Will be created and added to this company.
              </Typography>
            </Box>
            {companyConfirmDialog.existingCount > 0 && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#9a3412" }}>
                  • {companyConfirmDialog.existingCount} Existing User(s):
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Already exist in database and will be <strong>skipped</strong>.
                </Typography>
              </Box>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            Do you want to add these new users to this existing company, or cancel and use a different company name in your template?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={handleCancelExistingCompany}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: "none",
              color: "#64748b",
              borderColor: "#cbd5e1",
              "&:hover": { borderColor: "#94a3b8" },
            }}
          >
            Cancel & Change Company Name
          </Button>
          <Button
            onClick={handleConfirmExistingCompany}
            variant="contained"
            sx={{
              borderRadius: 2,
              textTransform: "none",
              bgcolor: "#19B880",
              "&:hover": { bgcolor: "#148f67" },
              fontWeight: 600,
            }}
          >
            Yes, Add Users to Existing Company
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};


export default AddData;
